import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Grid,

  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AppDatePicker, { INCOMPLETE_DATE_MESSAGE } from '@/components/common/AppDatePicker';
import { AdminService } from '@/services/admin.service';
import { ControlType, Issuer, UpdateControlTypeRequest } from '@/types';
import { CacheManager, CACHE_KEYS } from '@/utils/cache';
import { toDateOnlyString } from '@/utils/format';
import ControlPeriodSelect, { controlPeriodMonthsError, controlPeriodPayload } from '@/components/admin/ControlPeriodSelect';
import { DEFAULT_PERIOD_CONFIG, type ControlPeriodConfig } from '@shared/control-period';
import AllowedAffarspartnersSection from '@/components/admin/AllowedAffarspartnersSection';

interface EditControlTypeModalProps {
  open: boolean;
  controlType: ControlType | null;
  onClose: () => void;
  onSubmit: (id: string, data: UpdateControlTypeRequest) => Promise<void>;
  isLoading?: boolean;
}

const EditControlTypeModal: React.FC<EditControlTypeModalProps> = ({
  open,
  controlType,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<UpdateControlTypeRequest>({
    name: '',
    issuerId: '',
    description: '',
    isActive: true,
    activeFrom: '',
    activeUntil: '',
  });
  const [period, setPeriod] = useState<ControlPeriodConfig>(DEFAULT_PERIOD_CONFIG);

  const [issuers, setIssuers] = useState<Issuer[]>([]);
  const [isLoadingIssuers, setIsLoadingIssuers] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeFromDate, setActiveFromDate] = useState<Date | null>(null);
  const [activeUntilDate, setActiveUntilDate] = useState<Date | null>(null);
  // Half-typed dates are null to the form; they must not pass as empty.
  const [activeFromInvalid, setActiveFromInvalid] = useState(false);
  const [activeUntilInvalid, setActiveUntilInvalid] = useState(false);

  // Load selectable issuers when the modal opens.
  useEffect(() => {
    if (!open) return;
    setIsLoadingIssuers(true);
    AdminService.getIssuers()
      .then((response) => {
        setIssuers(Array.isArray(response?.issuers) ? response.issuers : []);
      })
      .catch((error) => {
        console.error('Failed to load issuers:', error);
        setIssuers([]);
      })
      .finally(() => setIsLoadingIssuers(false));
  }, [open]);

  // Reset form when modal opens or control type changes
  useEffect(() => {
    if (controlType && open) {
      setFormData({
        name: controlType.name,
        issuerId: controlType.issuerId,
        description: controlType.description,
        isActive: controlType.isActive,
        activeFrom: controlType.activeFrom,
        activeUntil: controlType.activeUntil || '',
      });
      setPeriod({
        periodMode: controlType.periodMode,
        periodYearOffset: controlType.periodYearOffset,
        periodMonths: controlType.periodMonths,
      });
      setActiveFromDate(new Date(controlType.activeFrom));
      setActiveUntilDate(controlType.activeUntil ? new Date(controlType.activeUntil) : null);
      setErrors({});
    }
  }, [controlType, open]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors['name'] = 'Namn är obligatoriskt';
    }

    if (!formData.issuerId) {
      newErrors['issuerId'] = 'Utfärdare är obligatorisk';
    }

    if (!formData.description?.trim()) {
      newErrors['description'] = 'Beskrivning är obligatorisk';
    }

    if (activeFromInvalid) {
      newErrors['activeFrom'] = INCOMPLETE_DATE_MESSAGE;
    } else if (!activeFromDate) {
      newErrors['activeFrom'] = 'Aktiv från-datum är obligatoriskt';
    }

    if (activeUntilInvalid) {
      newErrors['activeUntil'] = INCOMPLETE_DATE_MESSAGE;
    } else if (activeUntilDate && activeFromDate && activeUntilDate <= activeFromDate) {
      newErrors['activeUntil'] = 'Aktiv till-datum måste vara efter aktiv från-datum';
    }

    const monthsError = controlPeriodMonthsError(period);
    if (monthsError) {
      newErrors['periodMonths'] = monthsError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!controlType || !validateForm()) {
      return;
    }

    try {
      const submitData: UpdateControlTypeRequest = {
        ...formData,
        ...controlPeriodPayload(period),
        ...(activeFromDate && { activeFrom: toDateOnlyString(activeFromDate) }),
      };

      // null explicitly clears the end date (backend treats undefined as
      // "unchanged", so omitting the field would make it impossible to clear).
      submitData.activeUntil = activeUntilDate ? toDateOnlyString(activeUntilDate) : null;

      await onSubmit(controlType.id, submitData);
      
      // Clear the control types cache so the CreateControlPage will refresh
      CacheManager.remove(CACHE_KEYS.PREDEFINED_CONTROL_TYPES);

      handleClose();
    } catch (error) {
      console.error('Error updating control type:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      issuerId: '',
      description: '',
      isActive: true,
      activeFrom: '',
      activeUntil: '',
    });
    setPeriod(DEFAULT_PERIOD_CONFIG);
    setErrors({});
    setActiveFromDate(null);
    setActiveUntilDate(null);
    onClose();
  };

  const handleChange = (field: keyof UpdateControlTypeRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  if (!controlType) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { minHeight: '600px' }
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h5" component="div">
            Redigera kontrolltyp
          </Typography>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            {controlType.issuer.displayName} • Version {controlType.version}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Namn"
                  value={formData.name}
                  onChange={handleChange('name')}
                  error={!!errors['name']}
                  helperText={errors['name']}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Utfärdare"
                  value={formData.issuerId}
                  onChange={handleChange('issuerId')}
                  error={!!errors['issuerId']}
                  helperText={errors['issuerId'] || (isLoadingIssuers ? 'Laddar utfärdare...' : '')}
                  required
                  disabled={isLoadingIssuers}
                >
                  {isLoadingIssuers ? (
                    <MenuItem disabled>Laddar...</MenuItem>
                  ) : issuers.length === 0 ? (
                    <MenuItem disabled>Inga utfärdare tillgängliga</MenuItem>
                  ) : (
                    issuers.map((issuer) => (
                      <MenuItem key={issuer.id} value={issuer.id}>
                        {issuer.displayName} ({issuer.code})
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>

              {controlType && formData.issuerId !== controlType.issuerId && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">
                    <Typography variant="body2">
                      Om du byter utfärdare ändras även utfärdaren för alla befintliga
                      kontroller som använder denna kontrolltyp.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12, sm: 6 }}>
                <AppDatePicker
                  label="Aktiv från"
                  value={activeFromDate}
                  onChange={(newValue) => {
                    setActiveFromDate(newValue);
                    if (errors['activeFrom']) {
                      setErrors(prev => ({ ...prev, activeFrom: '' }));
                    }
                  }}
                  onInvalidChange={(invalid) => {
                    setActiveFromInvalid(invalid);
                    // Clearing the field fires no onChange (null to null), so
                    // the half-typed message is dropped here instead.
                    if (!invalid && errors['activeFrom'] === INCOMPLETE_DATE_MESSAGE) {
                      setErrors(prev => ({ ...prev, activeFrom: '' }));
                    }
                  }}
                  required
                  error={!!errors['activeFrom']}
                  helperText={errors['activeFrom']}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <AppDatePicker
                  label="Aktiv till (valfritt)"
                  value={activeUntilDate}
                  onChange={(newValue) => {
                    setActiveUntilDate(newValue);
                    if (errors['activeUntil']) {
                      setErrors(prev => ({ ...prev, activeUntil: '' }));
                    }
                  }}
                  onInvalidChange={(invalid) => {
                    setActiveUntilInvalid(invalid);
                    // Clearing the field fires no onChange (null to null), so
                    // the half-typed message is dropped here instead.
                    if (!invalid && errors['activeUntil'] === INCOMPLETE_DATE_MESSAGE) {
                      setErrors(prev => ({ ...prev, activeUntil: '' }));
                    }
                  }}
                  error={!!errors['activeUntil']}
                  helperText={errors['activeUntil'] || 'Lämna tomt för obegränsad giltighetstid'}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Beskrivning"
                  value={formData.description}
                  onChange={handleChange('description')}
                  error={!!errors['description']}
                  helperText={errors['description']}
                  required
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <ControlPeriodSelect
                  value={period}
                  onChange={(next) => {
                    setPeriod(next);
                    if (errors['periodMonths']) {
                      setErrors(prev => ({ ...prev, periodMonths: '' }));
                    }
                  }}
                  monthsError={errors['periodMonths']}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive || false}
                      onChange={handleChange('isActive')}
                      color="primary"
                    />
                  }
                  label="Aktiv"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Version kan inte ändras efter att kontrolltypen har skapats.
                    Formulärkonfiguration kommer att kunna redigeras i en framtida version.
                  </Typography>
                </Alert>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <AllowedAffarspartnersSection controlTypeId={controlType.id} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose}
            disabled={isLoading}
          >
            Avbryt
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Sparar...' : 'Spara ändringar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditControlTypeModal;